import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/common/PageHeader';
import { useAuth } from '../../context/AuthContext';
import { feedbackService } from '../../services';
import { validateEmail } from '../../utils/validation';

const Feedback = () => {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();

  const [name, setName] = useState(user?.fullName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const canSkipIdentity = useMemo(() => isAuthenticated() && !!user?.email, [isAuthenticated, user?.email]);

  const validate = () => {
    const errs = {};
    if (!canSkipIdentity) {
      if (email?.trim()) {
        const e = validateEmail(email);
        if (e) errs.email = e;
      }
    }
    if (!message.trim()) errs.message = t('form.validation.required');
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        name: name.trim() || undefined,
        email: email.trim().toLowerCase() || undefined,
        subject: subject.trim() || undefined,
        message: message.trim(),
      };
      await feedbackService.createFeedback(payload);
      setSuccess(t('feedback.success'));
      setSubject('');
      setMessage('');
    } catch (err) {
      setError(err.response?.data?.message || t('feedback.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary-50 pb-12">
      <PageHeader
        title={t('feedback.title')}
        subtitle={t('feedback.subtitle')}
        icon={
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h6m-6 8l-4-4V4a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2H7z" />
          </svg>
        }
      />

      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm">
              {success}
            </div>
          )}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {!canSkipIdentity && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('feedback.name')} <span className="text-gray-400 text-xs">{t('auth.optional')}</span>
                  </label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                    placeholder={t('feedback.namePlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('auth.email')} <span className="text-gray-400 text-xs">{t('auth.optional')}</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${fieldErrors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder={t('auth.emailPlaceholder')}
                  />
                  {fieldErrors.email && <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('feedback.subject')} <span className="text-gray-400 text-xs">{t('auth.optional')}</span>
              </label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                placeholder={t('feedback.subjectPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('feedback.message')} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${fieldErrors.message ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                placeholder={t('feedback.messagePlaceholder')}
                required
              />
              {fieldErrors.message && <p className="mt-1 text-xs text-red-600">{fieldErrors.message}</p>}
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 rounded-xl bg-primary-600 text-white font-bold hover:bg-primary-700 transition disabled:opacity-50"
              >
                {loading ? t('common.loading') : t('feedback.submit')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Feedback;

